import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./FastBoundConnect.scss";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import IUpdateSupplierIntegrationsDTO from "../../../../models/DTO/IUpdateSupplierIntegrationsDTO";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IFastbound_Account from "../../../../models/IFastbound_Account";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { OwAccordion } from "../../../shared/OwAccordion/OwAccordion";
import Modal from "react-modal";
import { FastBoundAccountModal } from "./FastBoundAccountModal/FastBoundAccountModal";
import FastBoundService from "../../../../services/FastBoundService";
import UserService from "../../../../services/UserService";
import { Socket } from "socket.io-client";

import moment from "moment";
import logo from "../../../../assets/images/fastbound/fastbound-logo.svg";

type Props = {
    socket: Socket;
    setActivityIndicator: (activityIndicator: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    isMainViewLight: boolean;
};

const getModalStyle = () => ({
    content: {
        top: "10px",
        left: "10px",
        right: "10px",
        bottom: "10px",
        color: "var(--main-text-color)",
        backgroundColor: "var(--main-bg-color)",
        border: "1px solid var(--minor-bg-color)",
    },
    overlay: {
        zIndex: 100,
    },
});

export const FastBoundConnect: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const hasFastBound = supplierModel.Supplier_Integration.has_fastbound;

    const [accounts, setAccounts] = useState<IFastbound_Account[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalStyle] = useState(getModalStyle());
    const [modalComponent, setModalComponent] = useState<React.ReactNode | null>(null);
    const [testingAccountId, setTestingAccountId] = useState<number | null>(null);
    const [deleteConfirmAccount, setDeleteConfirmAccount] = useState<IFastbound_Account | null>(null);

    useEffect(() => {
        props.socket.on(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);

        return () => {
            props.socket.removeListener(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
        };
    }, []);

    const onUpdateSupplierIntegrations = () => {
        loadUser();
    };

    const loadUser = () => {
        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
        };
        props.socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    const toggleFastBound = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: { has_fastbound: !supplierModel.Supplier_Integration.has_fastbound },
        };
        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const loadAccounts = async () => {
        try {
            const data = await FastBoundService.getAccounts(userModel, supplierModel);
            setAccounts(data);
        } catch (err: any) {
            console.error("Failed to load FastBound accounts:", err);
        }
    };

    const openAddModal = () => {
        const component = <FastBoundAccountModal closeModalFn={closeModal} notifyFn={props.notifyFn} />;
        setModalComponent(component);
        setModalIsOpen(true);
    };

    const openEditModal = (account: IFastbound_Account) => {
        const component = <FastBoundAccountModal closeModalFn={closeModal} notifyFn={props.notifyFn} account={account} />;
        setModalComponent(component);
        setModalIsOpen(true);
    };

    const closeModal = (reloadAccounts?: boolean) => {
        setModalIsOpen(false);
        setModalComponent(null);
        if (reloadAccounts) {
            loadAccounts();
            loadUser();
        }
    };

    const handleDeleteAccount = async (account: IFastbound_Account) => {
        try {
            await FastBoundService.deleteAccount(account.id, userModel, supplierModel);
            props.notifyFn(null, {
                message: "successfully_deleted_account",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            loadAccounts();
            loadUser();
        } catch (err: any) {
            props.notifyFn(null, {
                message: err.message || "failed_to_delete_account",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        }
    };

    const handleTestConnection = async (account: IFastbound_Account) => {
        setTestingAccountId(account.id);
        try {
            await FastBoundService.testAccountConnection(account.id, userModel, supplierModel);
            props.notifyFn(null, {
                message: "connection_successful",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
        } catch (err: any) {
            props.notifyFn(null, {
                message: err.message || "connection_failed",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setTestingAccountId(null);
            loadAccounts();
        }
    };

    return (
        <div className={"FastBoundConnect"}>
            <OwAccordion
                hideChevron={!hasFastBound}
                leftImage={logo}
                leftImageAlt={"FastBound"}
                leftImageHeight={30}
                titleClick={hasFastBound}
                fn={loadAccounts}
                rightElem={
                    <div className={"ms-auto d-flex align-items-center"}>
                        {hasFastBound && (supplierModel.Fastbound_Accounts?.length || 0) > 0 && (
                            <Link className={"btn btn-sm btn-outline-secondary me-2"} to={"/business/integrations/fastbound/dashboard"}>
                                <i className={"fad fa-chart-line me-2"} />
                                Sync Dashboard
                            </Link>
                        )}
                        {hasFastBound && <OwButton title={"add_account"} fn={openAddModal} className={"btn-primary me-2"} />}
                        <OwToggle
                            active={!!hasFastBound}
                            name={"toggleFastBound"}
                            fn={toggleFastBound}
                            label={""}
                            showDisableConfirm={true}
                        />
                    </div>
                }>
                <div>
                    {hasFastBound ? (
                        <div>
                            {/* Account List */}
                            <div className={"d-flex"}>
                                <div className="w-10r">{/* Actions header */}</div>
                                <div className="w-15r">
                                    <OwLabel title={"name"} isBold={true} />
                                </div>
                                <div className="w-10r">
                                    <OwLabel title={"account_number"} isBold={true} />
                                </div>
                                <div className="w-10r">
                                    <OwLabel title={"enabled"} isBold={true} />
                                </div>
                                <div className="w-15r">
                                    <OwLabel title={"status"} isBold={true} />
                                </div>
                                <div className="w-15r">
                                    <OwLabel title={"last_sync"} isBold={true} />
                                </div>
                                <div className="w-10r">
                                    <OwLabel title={"api_key_set"} isBold={true} />
                                </div>
                                <div className="w-15r">
                                    <OwLabel title={"audit_user_email"} isBold={true} />
                                </div>
                            </div>

                            {(accounts || []).map((account) => (
                                <div key={account.id} className={"d-flex w-100 align-items-center"}>
                                    <div className={"w-10r"}>
                                        <div className="btn-group btn-group-sm">
                                            <OwButton
                                                centerIconClass={"fad fa-pencil"}
                                                className={"btn-secondary"}
                                                hoverTitle={"Edit"}
                                                fn={() => openEditModal(account)}
                                            />
                                            <OwButton
                                                centerIconClass={"fad fa-trash-alt"}
                                                className={"btn-danger"}
                                                hoverTitle={"Delete"}
                                                fn={() => setDeleteConfirmAccount(account)}
                                            />
                                            <OwButton
                                                centerIconClass={testingAccountId === account.id ? "fas fa-spinner fa-spin" : "fad fa-plug"}
                                                className={"btn-info"}
                                                hoverTitle={"Test Connection"}
                                                fn={() => handleTestConnection(account)}
                                                disabled={testingAccountId === account.id}
                                            />
                                        </div>
                                    </div>
                                    <div className={"w-15r"}>{account.name || "—"}</div>
                                    <div className={"w-10r"}>{account.fastbound_account_number || "—"}</div>
                                    <div className={"w-10r"}>
                                        <OwLabel
                                            leftIconClass={account.fastbound_is_enabled ? "fas fa-check" : "fas fa-times"}
                                            className={account.fastbound_is_enabled ? "text-success" : "text-danger"}
                                        />
                                    </div>
                                    <div className="w-15r">
                                        <div className={"d-flex align-items-center"}>
                                            <i
                                                className={`fas fa-circle me-1 ${
                                                    account.fastbound_last_sync_status === "healthy"
                                                        ? "text-success"
                                                        : account.fastbound_last_sync_status === "unreachable"
                                                          ? "text-danger"
                                                          : "text-muted"
                                                }`}
                                                style={{ fontSize: "0.5rem" }}
                                            />
                                            <span>
                                                {account.fastbound_last_sync_status === "healthy"
                                                    ? "Connected"
                                                    : account.fastbound_last_sync_status === "unreachable"
                                                      ? "Unreachable"
                                                      : "Not checked"}
                                            </span>
                                        </div>
                                        <small className={"text-muted"}>
                                            {account.fastbound_last_sync_at ? moment(account.fastbound_last_sync_at).fromNow() : "Never"}
                                        </small>
                                    </div>
                                    <div className="w-15r">
                                        {account.fastbound_last_sync_at
                                            ? UserService.parseLastUpdated(account.fastbound_last_sync_at, true, true, true)
                                            : "N/A"}
                                    </div>
                                    <div className={"w-10r"}>
                                        <OwLabel
                                            leftIconClass={account.fastbound_api_key_set ? "fas fa-check" : "fas fa-times"}
                                            className={account.fastbound_api_key_set ? "text-success" : "text-danger"}
                                        />
                                    </div>
                                    <div className={"w-15r"}>{account.fastbound_audit_user_email || "—"}</div>
                                </div>
                            ))}

                            {accounts.length === 0 && (
                                <div className={"text-center text-muted mt-2"}>
                                    <OwLabel title={"no_accounts_configured"} leftIconClass={"fad fa-crosshairs"} />
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </OwAccordion>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={() => closeModal()}
                contentLabel="Orderwerks"
                shouldCloseOnOverlayClick={false}
                className={"modalContentClass"}
                overlayClassName={"modalOverlayClass"}
                style={{ ...modalStyle, content: { ...modalStyle.content, minWidth: "auto", width: "fit-content" } }}>
                {modalComponent}
            </Modal>

            <Modal
                isOpen={!!deleteConfirmAccount}
                onRequestClose={() => setDeleteConfirmAccount(null)}
                contentLabel="Confirm Delete"
                shouldCloseOnOverlayClick={true}
                className={"modalContentClass"}
                overlayClassName={"modalOverlayClass"}
                style={{
                    ...modalStyle,
                    content: { ...modalStyle.content, top: "30%", bottom: "auto", maxWidth: "400px", margin: "0 auto" },
                }}>
                <div className={"p-3 text-center"}>
                    <OwTitle title={"confirm_delete"} tag={"h5"} />
                    <p className={"mb-3 mt-4"}>
                        <OwLabel title={"confirm_delete_account"} className={"me-1"} />
                        <strong>{deleteConfirmAccount?.name}</strong>?
                    </p>
                    <div className={"d-flex justify-content-center gap-2 mt-4"}>
                        <OwButton
                            title={"delete"}
                            className={"btn-danger"}
                            fn={() => {
                                handleDeleteAccount(deleteConfirmAccount!);
                                setDeleteConfirmAccount(null);
                            }}
                        />
                        <OwButton title={"cancel"} className={"btn-outline-secondary"} fn={() => setDeleteConfirmAccount(null)} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};
